// src/screens/DatePicker.web.js
// Date and Time picker with gold/white calendar and clock icons on black background

import React, { useState, useEffect } from 'react';

export default function WebDatePicker({ value, onChange, min, max, style, disabled = false }) {
  // Split the datetime value into separate date and time strings
  const [dateValue, setDateValue] = useState('');
  const [timeValue, setTimeValue] = useState('');

  // Initialize date and time values from the prop. Keyed on the EPOCH, not
  // the Date object: parents that pass `new Date(ms)` each render (calendar
  // rows) must not reset a half-typed field on every re-render.
  const valueMs = value instanceof Date && !isNaN(value) ? value.getTime() : null;
  useEffect(() => {
    if (valueMs == null) return;
    const v = new Date(valueMs);
    // Format date as YYYY-MM-DD
    const year = v.getFullYear();
    const month = String(v.getMonth() + 1).padStart(2, '0');
    const day = String(v.getDate()).padStart(2, '0');
    setDateValue(`${year}-${month}-${day}`);

    // Format time as HH:MM
    const hours = String(v.getHours()).padStart(2, '0');
    const minutes = String(v.getMinutes()).padStart(2, '0');
    setTimeValue(`${hours}:${minutes}`);
  }, [valueMs]);

  // Format a Date as YYYY-MM-DD in local time (avoids UTC shift).
  const formatLocalDate = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const maxDateValue = max instanceof Date && !isNaN(max) ? formatLocalDate(max) : null;
  const minDateValue = min instanceof Date && !isNaN(min) ? formatLocalDate(min) : null;

  // Convert the React Native style object to a web-compatible style object
  const webStyle = {
    width: style?.width || '100%',
    height: style?.height || 50,
    backgroundColor: style?.backgroundColor || '#0f1115',
    color: style?.color || '#ffd166', // Gold text color
    padding: style?.padding || 12,
    borderRadius: style?.borderRadius || 10,
    fontSize: style?.fontSize || 16,
    border: `${style?.borderWidth || 1}px solid ${style?.borderColor || '#2a2f36'}`,
    boxSizing: 'border-box',
    colorScheme: 'dark',
    marginRight: 8,
  };

  const handleDateChange = (e) => {
    const newDateValue = e.target.value;
    setDateValue(newDateValue);
    updateDateTime(newDateValue, timeValue);
  };

  const handleTimeChange = (e) => {
    const newTimeValue = e.target.value;
    setTimeValue(newTimeValue);
    updateDateTime(dateValue, newTimeValue);
  };

  const updateDateTime = (date, time) => {
    if (date && time) {
      // Build the Date from local components — `new Date("YYYY-MM-DD")` parses
      // as UTC midnight and shifts back a day in negative-offset timezones.
      const [year, month, day] = date.split('-').map(Number);
      const [hours, minutes] = time.split(':').map(Number);
      const newDate = new Date(year, month - 1, day, hours, minutes, 0, 0);

      if (!isNaN(newDate.getTime())) {
        onChange(newDate);
      }
    }
  };

  return (
    <>
      {/* Custom CSS to style the calendar/clock icons as gold/white */}
      <style>{`
        .date-time-picker-container {
          display: flex;
          gap: 8px;
          width: 100%;
        }
        
        .date-time-picker-container input[type="date"],
        .date-time-picker-container input[type="time"] {
          flex: 1;
        }
        
        input[type="date"]::-webkit-calendar-picker-indicator,
        input[type="time"]::-webkit-calendar-picker-indicator {
          filter: invert(0.7) sepia(1) saturate(5) hue-rotate(10deg);
          cursor: pointer;
          opacity: 0.9;
        }
        
        input[type="date"]::-webkit-calendar-picker-indicator:hover,
        input[type="time"]::-webkit-calendar-picker-indicator:hover {
          filter: invert(0.9) sepia(1) saturate(5) hue-rotate(10deg);
          opacity: 1;
        }
        
        input[type="date"]::-webkit-datetime-edit-text,
        input[type="date"]::-webkit-datetime-edit-month-field,
        input[type="date"]::-webkit-datetime-edit-day-field,
        input[type="date"]::-webkit-datetime-edit-year-field,
        input[type="time"]::-webkit-datetime-edit-text,
        input[type="time"]::-webkit-datetime-edit-hour-field,
        input[type="time"]::-webkit-datetime-edit-minute-field {
          color: #ffd166;
        }
        
        input[type="date"]:focus,
        input[type="time"]:focus {
          outline: none;
          border-color: #ffd166;
        }
      `}</style>
      
      <div className="date-time-picker-container">
        <input
          type="date"
          style={webStyle}
          value={dateValue}
          min={minDateValue}
          max={maxDateValue}
          disabled={disabled}
          onChange={handleDateChange}
        />
        <input
          type="time"
          style={webStyle}
          value={timeValue}
          disabled={disabled}
          onChange={handleTimeChange}
        />
      </div>
    </>
  );
}